import React, { useState } from "react";
import {
  IonContent,
  IonPage,
  IonInput,
  IonButton,
  IonText,
  IonSpinner,
  IonItem,
  IonLabel,
  IonIcon,
  IonSegment,
  IonSegmentButton,
} from "@ionic/react";
import { shieldCheckmarkOutline, keyOutline } from "ionicons/icons";
import { useForm, Controller } from "react-hook-form";
import { useTwoFactorLogin } from "@/hooks/useAuth";
import { getErrorMessage } from "@/api/client";
import AppLogo from "@/components/AppLogo";
import "./Login.css";
import { Did } from "@/types";
import { COPYRIGHT_TEXT } from "@/config/config.ts";

interface TwoFactorForm {
  code: string;
  recovery_code: string;
}

interface TwoFactorChallengeProps {
  userId: Did;
  onBack: () => void;
}

const TwoFactorChallenge: React.FC<TwoFactorChallengeProps> = ({
  userId,
  onBack,
}) => {
  const twoFactorMutation = useTwoFactorLogin();
  const [codeType, setCodeType] = useState<"code" | "recovery_code">("code");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<TwoFactorForm>({
    defaultValues: {
      code: "",
      recovery_code: "",
    },
  });

  const onSubmit = async (data: TwoFactorForm) => {
    try {
      await twoFactorMutation.mutateAsync({
        user_id: userId,
        code: codeType === "code" ? data.code : undefined,
        recovery_code:
          codeType === "recovery_code" ? data.recovery_code : undefined,
      });
      // Navigation handled by useLogin hook
    } catch {
      // Error handled by mutation
    }
  };

  const handleClear = () => {
    reset();
  };

  const handleCodeTypeChange = (value: string) => {
    setCodeType(value as "code" | "recovery_code");
    reset();
  };

  return (
    <IonPage>
      <IonContent className="login-content" fullscreen>
        <div className="login-container">
          <div className="login-header">
            <AppLogo className="tw:max-w-[320px]" />
            <h1>Two-factor authenticatie</h1>
            <p>Voer je authenticatiecode in om door te gaan</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="login-form">
            <IonSegment
              value={codeType}
              onIonChange={(e) =>
                handleCodeTypeChange(e.detail.value as string)
              }
              className="two-factor-segment"
            >
              <IonSegmentButton value="code">
                <IonLabel>Authenticator code</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="recovery_code">
                <IonLabel>Herstelcode</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            {codeType === "code" ? (
              <>
                <IonItem className="login-input" lines="none">
                  <IonIcon icon={shieldCheckmarkOutline} slot="start" />
                  <Controller
                    name="code"
                    control={control}
                    rules={{
                      required: "Authenticator code is verplicht",
                      pattern: {
                        value: /^\d{6}$/,
                        message: "Code moet 6 cijfers zijn",
                      },
                    }}
                    render={({ field }) => (
                      <IonInput
                        {...field}
                        type="text"
                        placeholder="123456"
                        inputMode="numeric"
                        maxlength={6}
                        onIonInput={(e) => field.onChange(e.detail.value)}
                      />
                    )}
                  />
                </IonItem>
                {errors.code && (
                  <IonText color="danger" className="error-text">
                    <p>{errors.code.message}</p>
                  </IonText>
                )}
                <IonText color="medium" className="help-text">
                  <p>Voer de 6-cijferige code in van je authenticator app</p>
                </IonText>
              </>
            ) : (
              <>
                <IonItem className="login-input" lines="none">
                  <IonIcon icon={keyOutline} slot="start" />
                  <Controller
                    name="recovery_code"
                    control={control}
                    rules={{
                      required: "Herstelcode is verplicht",
                      minLength: {
                        value: 21,
                        message: "Herstelcode moet 21 karakters zijn",
                      },
                      maxLength: {
                        value: 21,
                        message: "Herstelcode moet 21 karakters zijn",
                      },
                    }}
                    render={({ field }) => (
                      <IonInput
                        {...field}
                        type="text"
                        placeholder="xxxxx-xxxxx-xxxxx-xxx"
                        maxlength={21}
                        onIonInput={(e) => field.onChange(e.detail.value)}
                      />
                    )}
                  />
                </IonItem>
                {errors.recovery_code && (
                  <IonText color="danger" className="error-text">
                    <p>{errors.recovery_code.message}</p>
                  </IonText>
                )}
                <IonText color="medium" className="help-text">
                  <p>Voer een van je 21-karakter herstelcodes in</p>
                </IonText>
              </>
            )}

            {twoFactorMutation.error && (
              <IonText color="danger" className="error-text">
                <p>{getErrorMessage(twoFactorMutation.error)}</p>
              </IonText>
            )}

            <div className="two-factor-buttons">
              <IonButton
                expand="block"
                type="submit"
                className="login-button"
                disabled={twoFactorMutation.isPending}
              >
                {twoFactorMutation.isPending ? (
                  <>
                    <IonSpinner name="crescent" />
                    <span style={{ marginLeft: "8px" }}>Verifiëren...</span>
                  </>
                ) : (
                  "Verifiëren"
                )}
              </IonButton>

              <IonButton
                expand="block"
                fill="outline"
                onClick={handleClear}
                disabled={twoFactorMutation.isPending}
              >
                Wissen
              </IonButton>

              <IonButton
                expand="block"
                fill="clear"
                onClick={onBack}
                disabled={twoFactorMutation.isPending}
              >
                Terug naar inloggen
              </IonButton>
            </div>
          </form>

          <div className="login-footer">
            <IonText color="medium">
              <p>{COPYRIGHT_TEXT}</p>
            </IonText>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default TwoFactorChallenge;
